import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import {
  ApiDataResponse,
  CreatePipelinePayload,
  DashboardTiles,
  EnrichLeadResponse,
  LeadPipeline,
  LeadPipelineStatus,
  LeadPipelineExportEntity,
  LeadPipelineListResponse,
  NotificationsListResponse,
  PaginatedListResponse,
  PaginationMeta,
  PipelineActivity,
  PipelineCompany,
  PipelineExport,
  PipelineLead,
  PipelineLeadDetail,
  PipelinePerson,
  PipelineRun,
  PipelineRunDetail,
  PreviewCountsPayload,
  PreviewCountsResponse,
  RunNowResponse,
  StartExportPayload,
  StartExportResponse,
  SubmitFeedbackPayload,
  UnlockEmailResponse,
  UpdateLeadStatusPayload,
  UpdatePipelinePayload,
  VeridionFilterPayload,
} from '../models/lead-pipeline.model';

/**
 * Single-entity endpoints are inconsistent: GET/PATCH return the `{ data }` envelope,
 * but POST /pipelines (create) returns the entity directly. Tolerate both so a bare
 * 201 body doesn't surface as `undefined` (which crashed the post-launch navigation).
 */
function unwrap<T>(res: ApiDataResponse<T> | T): T {
  return (res as ApiDataResponse<T>)?.data ?? (res as T);
}

/**
 * Paginated endpoints vary: some return `{ data: [], meta: {} }`, some put the
 * pagination fields at the top level (Laravel default: `{ data: [], current_page,
 * last_page, total, ... }`), some nest a resource collection (`{ data: { data: [],
 * current_page, ... } }`), and a bare array is possible too. Normalise all of these
 * to `{ data: T[], meta: PaginationMeta }` so consumers never read `meta` off
 * `undefined` (which was throwing `Cannot read properties of undefined`).
 */
function normalizeList<T>(res: any): PaginatedListResponse<T> {
  let data: T[];
  let metaSrc: any;
  if (Array.isArray(res)) {
    data = res;
    metaSrc = {};
  } else if (Array.isArray(res?.data?.data)) {
    data = res.data.data;
    metaSrc = res.data.meta ?? res.data;
  } else if (Array.isArray(res?.data)) {
    data = res.data;
    metaSrc = res.meta ?? res;
  } else {
    data = [];
    metaSrc = res ?? {};
  }
  const meta: PaginationMeta = {
    current_page: metaSrc?.current_page ?? 1,
    last_page: metaSrc?.last_page ?? 1,
    per_page: metaSrc?.per_page ?? data.length,
    total: metaSrc?.total ?? data.length,
    message: metaSrc?.message,
  };
  return { data, meta };
}

/**
 * The companies endpoint returns Veridion/Apollo-prefixed fields (`company_name`,
 * `company_employee_count`, `company_location_*`, `company_revenue`, …). Map them to
 * the flat `PipelineCompany` shape the UI consumes (API untouched).
 */
function normalizeCompany(c: any): PipelineCompany {
  const city = c.company_location_city || undefined;
  const region = c.company_location_region || undefined;
  const country = c.company_location_country || '';
  const location = [city, region || country].filter(Boolean).join(', ');
  const revenueRaw = c.company_revenue_numeric ?? c.company_revenue;
  const revenueNum = revenueRaw === null || revenueRaw === undefined || revenueRaw === '' ? null : Number(revenueRaw);
  return {
    uuid: c.uuid,
    name: c.company_name ?? c.name ?? '',
    domain: c.company_domain ?? '',
    website: c.company_website ?? '',
    industry: c.company_industry ?? '',
    employee_count: c.company_employee_count ?? 0,
    revenue: revenueNum !== null && Number.isFinite(revenueNum) ? revenueNum : null,
    country,
    location,
    status: c.status,
    persons_count: c.persons_count ?? 0,
    qualified_count: c.qualified_leads_count ?? c.qualified_count ?? 0,
    first_seen_at: c.first_processed_at ?? c.created_at ?? '',
    last_processed_at: c.last_processed_at ?? '',
    logo_url: c.apollo_company_data?.logo_url ?? c.logo_url ?? null,
    veridion_data: c.veridion_data ?? null,
  };
}

/**
 * The dashboard endpoint returns a bare body (no `data` envelope) with the metrics
 * nested under `tiles` as `{ value, sub, ... }` objects, plus a sibling
 * `daily_lead_generation_chart`. Flatten it to the `DashboardTiles` shape the UI reads
 * (scalar values, `daily_lead_generation_chart` folded in). API untouched.
 */
function normalizeDashboard(res: any): DashboardTiles {
  const r = res?.data ?? res ?? {};
  const t = r.tiles ?? {};
  const lr = t.last_run ?? {};
  return {
    companies_found: t.companies_found?.value ?? 0,
    companies_searched: {
      value: t.companies_searched?.value ?? 0,
      percent_complete: t.companies_searched?.percent_complete ?? 0,
    },
    persons_discovered: {
      value: t.persons_discovered?.value ?? 0,
      avg_per_company: t.persons_discovered?.avg_per_company ?? 0,
    },
    qualified_leads: {
      value: t.qualified_leads?.value ?? 0,
      by_icp_match: t.qualified_leads?.sub ?? '',
    },
    avg_icp_match: t.avg_icp_match?.value ?? 0,
    daily_progress: {
      current_day_count: t.daily_progress?.current_day_count ?? 0,
      leads_per_day: t.daily_progress?.leads_per_day ?? 0,
    },
    last_run: {
      at: lr.last_run_at ?? null,
      relative: lr.relative ?? null,
      label: lr.sub ?? null,
    },
    status: (t.status?.value ?? 'draft') as LeadPipelineStatus,
    daily_lead_generation_chart: Array.isArray(r.daily_lead_generation_chart) ? r.daily_lead_generation_chart : [],
  };
}

function asArray(v: any): any[] {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Persons endpoint returns `person_*`-prefixed columns; flatten to `PipelinePerson`. */
function normalizePerson(p: any): PipelinePerson {
  return {
    uuid: p.uuid,
    company_uuid: p.company_uuid ?? '',
    name: p.person_name ?? p.name ?? [p.person_first_name, p.person_last_name].filter(Boolean).join(' '),
    first_name: p.person_first_name ?? p.first_name ?? '',
    last_name: p.person_last_name ?? p.last_name ?? '',
    title: p.person_job_title ?? p.title ?? '',
    seniority: p.person_seniority ?? p.seniority,
    departments: asArray(p.person_departments ?? p.departments),
    linkedin_url: p.person_linkedin_url ?? p.linkedin_url ?? null,
    location: p.person_location ?? p.location ?? '',
    email_status: p.email_status,
    email: p.person_email ?? p.email ?? '',
    icp_score: p.icp_score ?? 0,
    match_breakdown: p.match_breakdown && typeof p.match_breakdown === 'object' ? p.match_breakdown : {},
    qualified: !!(p.is_qualified ?? p.qualified),
    first_seen_at: p.created_at ?? p.first_seen_at ?? '',
    photo_url: p.apollo_person_data?.photo_url ?? p.photo_url ?? null,
  };
}

/** Qualified-leads row → `PipelineLead` (fields mostly align; fill `qualified_at`/uuids). */
function normalizeLead(l: any): PipelineLead {
  return {
    uuid: l.uuid,
    pipeline_uuid: l.pipeline_uuid ?? '',
    person_uuid: l.person_uuid ?? '',
    company_uuid: l.company_uuid ?? '',
    person_name: l.person_name ?? '',
    person_job_title: l.person_job_title ?? '',
    person_email: l.person_email ?? '',
    person_linkedin_url: l.person_linkedin_url ?? null,
    person_photo_url: l.apollo_person_data?.photo_url ?? l.person_photo_url ?? null,
    company_name: l.company_name ?? '',
    company_domain: l.company_domain ?? '',
    company_logo_url: l.apollo_company_data?.logo_url ?? l.company_logo_url ?? null,
    icp_score: l.icp_score ?? 0,
    status: l.status,
    feedback: l.feedback ?? null,
    qualified_at: l.qualified_at ?? l.created_at ?? '',
    viewed_at: l.viewed_at ?? null,
    contacted_at: l.contacted_at ?? null,
    is_enhanced: !!l.is_enhanced,
    enhanced_report_id: l.enhanced_report_id ?? null,
  };
}

/** Lead detail → `PipelineLeadDetail` (adds ICP/enrichment fields; coerces JSON arrays). */
function normalizeLeadDetail(l: any): PipelineLeadDetail {
  const revenue = l.company_revenue == null || l.company_revenue === '' ? null : Number(l.company_revenue);
  return {
    ...normalizeLead(l),
    person_first_name: l.person_first_name ?? '',
    person_last_name: l.person_last_name ?? '',
    person_seniority: l.person_seniority,
    company_industry: l.company_industry ?? '',
    company_employee_count: l.company_employee_count ?? 0,
    company_revenue: revenue !== null && Number.isFinite(revenue) ? revenue : null,
    match_breakdown: l.match_breakdown && typeof l.match_breakdown === 'object' ? l.match_breakdown : {},
    match_factors: asArray(l.match_factors),
    talking_points: asArray(l.talking_points),
    why_this_lead: l.why_this_lead ?? '',
    decision_trace: asArray(l.decision_trace),
    draft_email_subject: l.draft_email_subject ?? '',
    draft_email_body: l.draft_email_body ?? '',
    feedback_notes: l.feedback_notes ?? null,
  };
}

/** Query params for the pipeline list endpoint. */
export interface ListPipelinesParams {
  status?: string;
  workspace_id?: number;
  sort?: string;
  page?: number;
  include_archived?: boolean;
}

/** Query params for the long-list sub-resource endpoints (companies/persons/leads/activity/runs). */
export interface SubResourceListParams {
  [key: string]: string | number | boolean | undefined;
}

/**
 * One method per backend route under `/api/v1/pipelines*`.
 *
 * Mirrors `VeridionExportService`: auth header via `AuthService.token()`, base
 * URL `environment.api.url` (which already includes `/api/v1`). The service does
 * no business logic — it unwraps the standard `{ data, meta, tabs, metadata }`
 * envelope and returns typed payloads; effects own retries, polling and toasts.
 *
 * Source of truth: `api.salesommo.com` controllers + `resources/docs/1.0/pipelines/*`.
 */
@Injectable({ providedIn: 'root' })
export class LeadPipelineService {
  private apiUrl = environment.api.url;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  private getHttpOptions(extraHeaders?: Record<string, string>): Observable<{ headers: HttpHeaders }> {
    return this.authService.token().pipe(
      map(token => ({
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(extraHeaders ?? {}),
        }),
      })),
    );
  }

  private toHttpParams(params?: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }
    return httpParams;
  }

  // ---------------------------------------------------------------------------
  // Wizard preview-counts
  // ---------------------------------------------------------------------------

  /**
   * Live wizard counters. Sends the `X-Wizard-Session-Id` header (read by the
   * backend `LeadPipelinesController::previewCounts`) so the per-session unique
   * payload cap is enforced. Debounce is the caller's responsibility (effect).
   */
  previewCounts(payload: VeridionFilterPayload, wizardSessionId: string): Observable<PreviewCountsResponse> {
    const url = `${this.apiUrl}/pipelines/preview-counts`;
    const body: PreviewCountsPayload = { filters: payload };
    return this.getHttpOptions({ 'X-Wizard-Session-Id': wizardSessionId }).pipe(
      switchMap(options =>
        this.http.post<ApiDataResponse<PreviewCountsResponse>>(url, body, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  // ---------------------------------------------------------------------------
  // Pipeline CRUD & lifecycle
  // ---------------------------------------------------------------------------

  list(params?: ListPipelinesParams): Observable<LeadPipelineListResponse> {
    const url = `${this.apiUrl}/pipelines`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http
          .get<LeadPipelineListResponse>(url, { ...options, params: this.toHttpParams(params) })
          .pipe(catchError(error => throwError(() => error))),
      ),
    );
  }

  get(uuid: string): Observable<LeadPipeline> {
    const url = `${this.apiUrl}/pipelines/${uuid}`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<ApiDataResponse<LeadPipeline>>(url, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  create(payload: CreatePipelinePayload): Observable<LeadPipeline> {
    const url = `${this.apiUrl}/pipelines`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<ApiDataResponse<LeadPipeline>>(url, payload, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  update(uuid: string, payload: UpdatePipelinePayload): Observable<LeadPipeline> {
    const url = `${this.apiUrl}/pipelines/${uuid}`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.patch<ApiDataResponse<LeadPipeline>>(url, payload, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  delete(uuid: string): Observable<void> {
    const url = `${this.apiUrl}/pipelines/${uuid}`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.delete<unknown>(url, options).pipe(
          map(() => undefined),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  pause(uuid: string): Observable<LeadPipeline> {
    return this.lifecycleAction(uuid, 'pause');
  }

  resume(uuid: string): Observable<LeadPipeline> {
    return this.lifecycleAction(uuid, 'resume');
  }

  archive(uuid: string): Observable<LeadPipeline> {
    return this.lifecycleAction(uuid, 'archive');
  }

  private lifecycleAction(uuid: string, action: 'pause' | 'resume' | 'archive'): Observable<LeadPipeline> {
    const url = `${this.apiUrl}/pipelines/${uuid}/${action}`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<ApiDataResponse<LeadPipeline>>(url, {}, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  /**
   * Manual run. Treats HTTP 202 (a run is already in flight, no charge) as a
   * non-error response and flags `in_flight: true` so the reducer can flip
   * `runNow.inFlight[uuid]` without surfacing a toast error.
   */
  runNow(uuid: string): Observable<RunNowResponse> {
    const url = `${this.apiUrl}/pipelines/${uuid}/run-now`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http
          .post<any>(url, {}, { ...options, observe: 'response' })
          .pipe(
            map(response => {
              // The run-now endpoint returns fields at the top level (no `data`
              // envelope): 200 -> { uuid, status, credits_consumed, ... }, and
              // 202 -> { in_flight_run_uuid, message }. Map them onto
              // RunNowResponse so run_uuid is populated and the completion poll
              // (getRun) works.
              const body: any = response.body ?? {};
              const inFlight = response.status === 202;
              return {
                run_uuid: inFlight ? body.in_flight_run_uuid : body.uuid,
                status: inFlight ? 'in_flight' : 'queued',
                credits_deducted: body.credits_consumed,
                in_flight: inFlight,
              } as RunNowResponse;
            }),
            catchError(error => throwError(() => error)),
          ),
      ),
    );
  }

  // ---------------------------------------------------------------------------
  // Dashboard
  // ---------------------------------------------------------------------------

  /**
   * Dashboard tiles + chart. The chart is part of this single endpoint
   * (`daily_lead_generation_chart`); changing the range just re-fetches with a
   * different `chart_range`. There is no separate chart endpoint.
   */
  getDashboard(uuid: string, chartRange: '7d' | '30d' | '90d' = '30d'): Observable<DashboardTiles> {
    const url = `${this.apiUrl}/pipelines/${uuid}/dashboard`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http
          .get<any>(url, {
            ...options,
            params: this.toHttpParams({ chart_range: chartRange }),
          })
          .pipe(
            map(res => normalizeDashboard(res)),
            catchError(error => throwError(() => error)),
          ),
      ),
    );
  }

  // ---------------------------------------------------------------------------
  // Companies / Persons
  // ---------------------------------------------------------------------------

  listCompanies(uuid: string, params?: SubResourceListParams): Observable<PaginatedListResponse<PipelineCompany>> {
    return this.getList<any>(`${this.apiUrl}/pipelines/${uuid}/companies`, params).pipe(
      map(res => ({ data: res.data.map(normalizeCompany), meta: res.meta })),
    );
  }

  listPersons(uuid: string, params?: SubResourceListParams): Observable<PaginatedListResponse<PipelinePerson>> {
    return this.getList<any>(`${this.apiUrl}/pipelines/${uuid}/persons`, params).pipe(
      map(res => ({ data: res.data.map(normalizePerson), meta: res.meta })),
    );
  }

  unlockEmail(uuid: string, personUuid: string): Observable<UnlockEmailResponse> {
    const url = `${this.apiUrl}/pipelines/${uuid}/persons/${personUuid}/unlock-email`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<ApiDataResponse<UnlockEmailResponse>>(url, {}, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  // ---------------------------------------------------------------------------
  // Qualified leads
  // ---------------------------------------------------------------------------

  listLeads(uuid: string, params?: SubResourceListParams): Observable<PaginatedListResponse<PipelineLead>> {
    return this.getList<any>(`${this.apiUrl}/pipelines/${uuid}/leads`, params).pipe(
      map(res => ({ data: res.data.map(normalizeLead), meta: res.meta })),
    );
  }

  getLead(uuid: string, leadUuid: string): Observable<PipelineLeadDetail> {
    const url = `${this.apiUrl}/pipelines/${uuid}/leads/${leadUuid}`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, options).pipe(
          map(res => normalizeLeadDetail(res?.data ?? res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  enhanceLead(uuid: string, leadUuid: string): Observable<ApiDataResponse<unknown>> {
    return this.postAction(`${this.apiUrl}/pipelines/${uuid}/leads/${leadUuid}/enhance`, {});
  }

  enrichForWorkspace(uuid: string, leadUuid: string, workspaceId: number): Observable<EnrichLeadResponse> {
    const url = `${this.apiUrl}/pipelines/${uuid}/leads/${leadUuid}/enrich`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http
          .post<ApiDataResponse<EnrichLeadResponse>>(url, { workspace_id: workspaceId }, options)
          .pipe(
            map(res => unwrap(res)),
            catchError(error => throwError(() => error)),
          ),
      ),
    );
  }

  copyToCrm(uuid: string, leadUuid: string): Observable<ApiDataResponse<unknown>> {
    return this.postAction(`${this.apiUrl}/pipelines/${uuid}/leads/${leadUuid}/copy-to-crm`, {});
  }

  updateLeadStatus(uuid: string, leadUuid: string, payload: UpdateLeadStatusPayload): Observable<PipelineLead> {
    const url = `${this.apiUrl}/pipelines/${uuid}/leads/${leadUuid}/status`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.patch<ApiDataResponse<PipelineLead>>(url, payload, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  submitFeedback(uuid: string, leadUuid: string, payload: SubmitFeedbackPayload): Observable<PipelineLead> {
    const url = `${this.apiUrl}/pipelines/${uuid}/leads/${leadUuid}/feedback`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<ApiDataResponse<PipelineLead>>(url, payload, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  /** Phase 2 stub on the backend (returns 202 + recorded intent). */
  sendEmail(uuid: string, leadUuid: string): Observable<ApiDataResponse<unknown>> {
    return this.postAction(`${this.apiUrl}/pipelines/${uuid}/leads/${leadUuid}/send-email`, {});
  }

  // ---------------------------------------------------------------------------
  // Activity / Runs / Notifications
  // ---------------------------------------------------------------------------

  listActivity(uuid: string, params?: SubResourceListParams): Observable<PaginatedListResponse<PipelineActivity>> {
    return this.getList<PipelineActivity>(`${this.apiUrl}/pipelines/${uuid}/activity`, params);
  }

  listRuns(uuid: string, params?: SubResourceListParams): Observable<PaginatedListResponse<PipelineRun>> {
    return this.getList<PipelineRun>(`${this.apiUrl}/pipelines/${uuid}/runs`, params);
  }

  getRun(uuid: string, runUuid: string): Observable<PipelineRunDetail> {
    const url = `${this.apiUrl}/pipelines/${uuid}/runs/${runUuid}`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        // The runs endpoint returns the run at the top level (no `data` envelope);
        // unwrap tolerates both shapes so the run-now completion poll sees `status`.
        this.http.get<ApiDataResponse<PipelineRunDetail> | PipelineRunDetail>(url, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  listNotifications(uuid: string, params?: SubResourceListParams): Observable<NotificationsListResponse> {
    const url = `${this.apiUrl}/pipelines/${uuid}/notifications`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http
          .get<NotificationsListResponse>(url, { ...options, params: this.toHttpParams(params) })
          .pipe(catchError(error => throwError(() => error))),
      ),
    );
  }

  // ---------------------------------------------------------------------------
  // Exports
  // ---------------------------------------------------------------------------

  /** Kicks off an async CSV export; returns the export descriptor (status `pending`). */
  exportCsv(
    uuid: string,
    entity: LeadPipelineExportEntity,
    filtersApplied?: Record<string, unknown>,
  ): Observable<StartExportResponse> {
    const url = `${this.apiUrl}/pipelines/${uuid}/${entity}/export`;
    const body: StartExportPayload = filtersApplied ? { filters: filtersApplied } : {};
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<ApiDataResponse<StartExportResponse>>(url, body, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  listExports(uuid: string): Observable<PaginatedListResponse<PipelineExport>> {
    return this.getList<PipelineExport>(`${this.apiUrl}/pipelines/${uuid}/exports`);
  }

  /** Poll an export by its uuid (cross-pipeline route `/exports/{uuid}`). */
  getExport(exportUuid: string): Observable<PipelineExport> {
    const url = `${this.apiUrl}/exports/${exportUuid}`;
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<ApiDataResponse<PipelineExport>>(url, options).pipe(
          map(res => unwrap(res)),
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }

  /**
   * URL for the 302-redirect download route. Primary download path is the signed
   * `download_url` on a `ready` export (no auth needed); this is the fallback.
   */
  getExportDownloadUrl(exportUuid: string): string {
    return `${this.apiUrl}/exports/${exportUuid}/download`;
  }

  // ---------------------------------------------------------------------------
  // Shared helpers
  // ---------------------------------------------------------------------------

  private getList<T>(url: string, params?: SubResourceListParams): Observable<PaginatedListResponse<T>> {
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http
          .get<any>(url, { ...options, params: this.toHttpParams(params) })
          .pipe(
            map(res => normalizeList<T>(res)),
            catchError(error => throwError(() => error)),
          ),
      ),
    );
  }

  private postAction(url: string, body: unknown): Observable<ApiDataResponse<unknown>> {
    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<ApiDataResponse<unknown>>(url, body, options).pipe(
          catchError(error => throwError(() => error)),
        ),
      ),
    );
  }
}
